const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const apiKey = localStorage.getItem('apiKey');
    const token = localStorage.getItem('token');
    
    const isDev = process.env.NODE_ENV === 'development';

    if (isDev) {
      console.log('🔐 API Request - Endpoint:', endpoint);
    }
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        // Usar API Key para todos los usuarios
        ...(apiKey && { 'x-api-key': apiKey }),
        // Agregar token Bearer si está disponible (para endpoints que lo requieran)
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    };

    const config = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    if (isDev) {
      console.log('🔐 API Request - Token presente:', !!token);
    }

    try {
      const response = await fetch(url, config);
      
      // Manejar respuestas vacías o sin contenido JSON
      let data = {};
      const contentType = response.headers.get('content-type');
      
      // Intentar parsear como JSON si el content-type lo indica o si no hay content-type especificado
      if (!contentType || contentType.includes('application/json')) {
        try {
          const text = await response.text();
          data = text ? JSON.parse(text) : {};
        } catch (parseError) {
          // Si falla el parseo, probablemente es una respuesta vacía o no es JSON válido
          console.warn('Error parsing JSON response:', parseError);
          data = {};
        }
      }

      if (!response.ok) {
        const errorMessage = data.error || data.message || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.response = {
          data: data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        };
        throw error;
      }

      // Retornar en formato similar a axios para compatibilidad
      return {
        data: data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      };
    } catch (error) {
      console.error('API request failed:', error);
      // Si el error ya tiene response, re-lanzarlo tal cual
      if (error.response) {
        throw error;
      }
      // Si es un error de parsing JSON u otro error, crear un objeto de error similar a axios
      const axiosLikeError = new Error(error.message || 'API request failed');
      axiosLikeError.response = {
        data: { error: error.message },
        status: 0,
        statusText: 'Network Error'
      };
      throw axiosLikeError;
    }
  }

  // Métodos helper para compatibilidad con axios
  async get(endpoint, config = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...config
    });
  }

  async post(endpoint, data = {}, config = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...config
    });
  }

  async put(endpoint, data = {}, config = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...config
    });
  }

  async delete(endpoint, config = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...config
    });
  }

  // Meeting endpoints
  async createMeeting(meetingData) {
    const response = await this.request('/api/meetings', {
      method: 'POST',
      body: JSON.stringify(meetingData),
    });
    return response.data;
  }

  async getMeeting(meetingId) {
    const response = await this.request(`/api/meetings/${meetingId}`);
    return response.data;
  }

  async getMeetings() {
    const response = await this.request('/api/meetings');
    return response.data;
  }

  async deleteMeeting(meetingId, data = {}) {
    const response = await this.request(`/api/meetings/${meetingId}`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async endMeeting(meetingId, data = {}) {
    // Usar DELETE para terminar la reunión (compatible con el endpoint existente)
    const response = await this.request(`/api/meetings/${meetingId}`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async createAttendee(meetingId, attendeeData) {
    const response = await this.request(`/api/meetings/${meetingId}/attendees`, {
      method: 'POST',
      body: JSON.stringify(attendeeData),
    });
    return response.data;
  }

  // Transcription endpoints
  async createTranscription(transcriptionData) {
    const response = await this.request('/api/transcriptions', {
      method: 'POST',
      body: JSON.stringify(transcriptionData),
    });
    return response.data;
  }

  async getTranscriptions() {
    const response = await this.request('/api/transcriptions');
    return response.data;
  }

  async getTranscription(transcriptionId) {
    const response = await this.request(`/api/transcriptions/${transcriptionId}`);
    return response.data;
  }

  async getTranscriptionsByMeeting(meetingId) {
    const response = await this.request(`/api/transcriptions/meeting/${meetingId}`);
    return response.data;
  }

  async saveSegment(transcriptionId, segment, attendeeId) {
    const response = await this.request(`/api/transcriptions/${transcriptionId}/segments`, {
      method: 'POST',
      body: JSON.stringify({ segment, attendeeId }),
    });
    return response.data;
  }

  async processTranscriptionWithGPT(transcriptionId) {
    const response = await this.request(`/api/transcriptions/${transcriptionId}/gpt`, {
      method: 'POST',
    });
    return response.data;
  }

  // Document endpoints
  async getDocumentsByCita(citaId) {
    const response = await this.request(`/api/documentos/cita/${citaId}`);
    return response.data;
  }

  // Cita endpoints
  async getCita(citaId) {
    const response = await this.request(`/api/cita/${citaId}`);
    return response.data;
  }

  // Endpoints públicos para pacientes (con API Key)
  async getPublicMeeting(meetingId) {
    const response = await this.request(`/api/meetings/public/${meetingId}`);
    return response.data;
  }

  async joinPublicMeeting(meetingId, patientData) {
    const response = await this.request(`/api/meetings/public/${meetingId}/join`, {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
    return response.data;
  }

  // Método para establecer API Key
  setApiKey(apiKey) {
    localStorage.setItem('apiKey', apiKey);
  }

  // Método para limpiar autenticación
  clearAuth() {
    localStorage.removeItem('apiKey');
  }
}

const api = new ApiService();
export default api;
